import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicNavbar from "@/components/PublicNavbar";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const isAdminOrCoach = user?.role === "admin" || user?.role === "coach";
      const subscriptionTier = user?.subscription_tier;
      const hasActiveSubscription = subscriptionTier &&
        subscriptionTier !== "free" &&
        subscriptionTier !== "none" &&
        subscriptionTier !== "";
      if (isAdminOrCoach || hasActiveSubscription) {
        navigate("/dashboard");
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const magicToken = params.get('magic_token');
    const remember = params.get('remember') === 'true';
    if (magicToken) {
      verifyMagicLink(magicToken, remember);
    }
  }, []);

  const verifyMagicLink = async (token, remember = false) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/magic-link/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, remember_me: remember }),
      });
      const data = await response.json();
      if (response.ok) {
        login(data.user);
        window.history.replaceState({}, document.title, window.location.pathname);
        const isAdminOrCoach = data.user?.role === "admin" || data.user?.role === "coach";
        const subscriptionTier = data.user?.subscription_tier;
        const hasActiveSubscription = subscriptionTier &&
          subscriptionTier !== "free" &&
          subscriptionTier !== "none" &&
          subscriptionTier !== "";
        if (isAdminOrCoach || hasActiveSubscription) {
          toast.success("Welcome to Paladin!");
          navigate("/dashboard");
        } else {
          toast.info("Please choose a membership plan to continue");
          navigate("/pricing");
        }
      } else {
        toast.error(data.detail || "Invalid or expired link");
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error("Connection error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/magic-link/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim(), remember_me: rememberMe, origin_url: window.location.origin }),
      });
      const data = await response.json();
      if (response.ok) {
        setLinkSent(true);
        toast.success("Check your email for the login link!");
      } else {
        toast.error(data.detail || "Failed to send login link");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const params = new URLSearchParams(window.location.search);
  if (params.get('magic_token') && loading) {
    return (
      <div className="min-h-screen bg-[#070B14] flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Signing you in...</p>
        </motion.div>
      </div>
    );
  }

  if (linkSent) {
    return (
      <div className="min-h-screen bg-[#070B14] text-white">
        <PublicNavbar />
        <div className="pt-32 flex items-center justify-center px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
            <div className="bg-[#0B0F1A] border border-white/10 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-blue-400" />
              </div>
              <h2 className="font-bold text-2xl mb-2" data-testid="check-email-heading">Check Your Email</h2>
              <p className="text-gray-400 mb-6">
                We sent a login link to<br/>
                <span className="text-white font-medium">{email}</span>
              </p>
              <p className="text-gray-500 text-sm mb-6">
                The link expires in 15 minutes. If you don't see it, check your spam folder.
              </p>
              <button onClick={() => setLinkSent(false)} className="text-blue-400 hover:underline text-sm">
                Use a different email
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070B14] text-white">
      <PublicNavbar />
      <div className="pt-32 flex items-center justify-center px-6 pb-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-bold text-3xl mb-2">SIGN IN</h1>
            <p className="text-gray-400">Continue your training</p>
          </div>
          <div className="bg-[#0B0F1A] border border-white/10 rounded-2xl p-8">
            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 bg-white text-black py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors mb-6"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#0B0F1A] text-gray-500">or continue with email</span>
              </div>
            </div>
            <form onSubmit={handleMagicLinkRequest} className="space-y-4">
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#141B2D] border border-[#1E293B] rounded-lg px-4 py-3 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
                data-testid="login-email"
                required
              />
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="sr-only"
                    data-testid="remember-me-checkbox"
                  />
                  <div className={`w-5 h-5 rounded border-2 transition-colors flex items-center justify-center ${
                    rememberMe
                      ? 'bg-blue-500 border-blue-500'
                      : 'border-[#2D3B4F] group-hover:border-zinc-500'
                  }`}>
                    {rememberMe && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <span className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">
                  Remember me for 30 days
                </span>
              </label>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="magic-link-btn"
              >
                {loading ? "Sending..." : (<><Mail className="w-5 h-5" /> Send Login Link</>)}
              </button>
              <p className="text-center text-gray-500 text-sm">
                No password needed. We'll email you a secure login link.
              </p>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
